import React, { useContext, useMemo } from "react"
import { Autocomplete, Box, InfoIcon, Link, TextField, Typography } from "@carrotfertility/carotene-core"
import { useController, useFormState } from "@carrotfertility/carotene-core-x"
import StarIcon from "@mui/icons-material/Star"
import { createFilterOptions } from "@mui/material/Autocomplete"
import type { RegisterOptions } from "react-hook-form"
import { RequiredItemModalsContext } from "./RequiredItemModalsContext"
import {
  SelectOption,
  getCaptionText,
  getDisabledOrWarningMessage,
  getRecommendedMessage,
  REQUIRED_BY_GREGBOT_PREFIX
} from "./carrotPlanAutocompleteHelpers"
import { FormResponsiveDisplayStatus } from "./hooks/useCarrotPlanMappings"
import { DisplayStatus } from "types/CarrotPlanOptions"

type StepOptionSelectProps = {
  name: string
  options: SelectOption[]
  disabled?: boolean
  placeholder?: string
  groupBy?: (option: SelectOption) => string
  rules?: RegisterOptions
  onInputChange?: (event: React.SyntheticEvent | null, value: string, reason: string) => void
  onUserChange?: () => void
  confirmRemovalOfRequired?: boolean
}

function getOptionIcon(option: SelectOption): React.ReactNode {
  const disabledOrWarning = getDisabledOrWarningMessage(option)
  const recommended = getRecommendedMessage(option)
  if (!disabledOrWarning && !recommended) return null
  return disabledOrWarning ? <InfoIcon sx={{ zIndex: 0 }} /> : <StarIcon sx={{ zIndex: 0 }} />
}

function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: SelectOption, name: string): React.ReactNode {
  const caption = getCaptionText(option)
  return (
    <Box
      component="li"
      gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      {...props}
      key={option.group + name + option.label + option.value}
    >
      {getOptionIcon(option)}
      <Box flexDirection="column">
        <Typography>{option.label}</Typography>
        {caption && (
          <Typography variant="caption" sx={{ fontStyle: "italic" }}>
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export function StepOptionSelect({
  name,
  options,
  disabled,
  placeholder,
  groupBy,
  rules,
  onInputChange,
  onUserChange,
  confirmRemovalOfRequired = false
}: StepOptionSelectProps): JSX.Element {
  const { openConfirmRemovalModal, openRequiredItemDetails } = useContext(RequiredItemModalsContext) ?? {}
  const { field, fieldState } = useController({ name, rules })
  const { isSubmitting } = useFormState()

  const filterOptions = useMemo(
    () =>
      createFilterOptions<SelectOption>({
        stringify: (option) => `${option.group ?? ""} ${option.label}`.trim()
      }),
    []
  )

  function handleChange(_: React.SyntheticEvent, newValue: SelectOption | null) {
    if (confirmRemovalOfRequired) {
      const previous = field.value ?? null
      const isChangingAwayFromRequired =
        previous?.optionMetadata?.isRequired && (newValue == null || newValue.value !== previous.value)
      if (isChangingAwayFromRequired) {
        openConfirmRemovalModal?.(() => {
          field.onChange(newValue)
          onUserChange?.()
        })
        return
      }
    }
    field.onChange(newValue)
    onUserChange?.()
  }

  const hasError = !!fieldState.error

  function getHelperText(): React.ReactNode {
    if (fieldState.error?.message) return fieldState.error.message
    const currentVal = field.value
    if (!currentVal) return ""
    if (currentVal.optionMetadata?.isRequired) {
      return (
        <>
          {REQUIRED_BY_GREGBOT_PREFIX}
          {openRequiredItemDetails && (
            <Link
              component="button"
              type="button"
              variant="inherit"
              color="inherit"
              onClick={() => openRequiredItemDetails(currentVal.optionMetadata?.requiredNote ?? "")}
              sx={{ cursor: "pointer", textDecorationThickness: "1px" }}
            >
              See more.
            </Link>
          )}
        </>
      )
    }
    return getCaptionText(currentVal)
  }

  return (
    <Autocomplete
      disablePortal
      fullWidth
      data-no-dnd="true"
      size="small"
      autoHighlight
      ListboxProps={{ style: { maxHeight: "30rem" } }}
      data-testid={name}
      options={options}
      value={field.value ?? null}
      onChange={handleChange}
      onBlur={field.onBlur}
      onInputChange={onInputChange}
      disabled={isSubmitting || disabled}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionDisabled={(option: SelectOption) =>
        option?.optionMetadata?.displayStatus === DisplayStatus.DISABLE ||
        option?.optionMetadata?.displayStatus === FormResponsiveDisplayStatus.DUPLICATE_ON_SAME_PLAN
      }
      filterOptions={filterOptions}
      groupBy={groupBy ?? ((option: SelectOption) => option.group)}
      renderOption={(props, option) => renderOption(props, option, name)}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={field.ref}
          error={hasError}
          helperText={getHelperText()}
          placeholder={placeholder}
          name={name}
        />
      )}
    />
  )
}
